import React, { useEffect, useState } from 'react';

import { Asset, Button, ChevronIcon } from '@clearcover/atlas';

import { InstallmentNavigatorProps } from './types';
import styles from './styles.module.css';

const defaultFormatDate = (dateString: string) => {
  const d = new Date(dateString);

  if (isNaN(d.getTime())) return dateString;

  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();

  return `${mm}/${dd}/${yyyy}`;
};

export const InstallmentNavigator: React.FC<InstallmentNavigatorProps> = ({
  installments,
  initialIndex = 0,
  onChangeIndex,
  formatDate = defaultFormatDate,
  'data-testid': dataTestId = 'payment-schedule-navigator',
}) => {
  const [index, setIndex] = useState(() =>
    Math.min(Math.max(initialIndex, 0), Math.max(installments.length - 1, 0))
  );

  useEffect(() => {
    if (installments.length === 0) return;

    if (onChangeIndex) {
      onChangeIndex(index, installments[index]);
    }
  }, [index, installments, onChangeIndex]);

  if (!installments) {
    return null;
  }

  const canPrev = index > 0;
  const canNext = index < installments.length - 1;

  const goPrev = () => {
    if (!canPrev) return;

    setIndex((i) => i - 1);
  };

  const goNext = () => {
    if (!canNext) return;

    setIndex((i) => i + 1);
  };

  const current = installments[index];

  return (
    <div className={styles.navigatorContainer} data-testid={dataTestId}>
      <div className={styles.navigatorRow}>
        <Button
          type="button"
          variant="secondary"
          className={styles.navButton}
          onClick={goPrev}
          disabled={!canPrev}
          aria-label="Previous installment"
          data-testid="prev-installment"
        >
          <span>
            <Asset
              icon={ChevronIcon}
              height={16}
              width={16}
              description="previous"
            />
          </span>
        </Button>
        <div className={styles.installmentCard} data-testid="installment-card">
          <span className={styles.amount}>{current.amount}</span>
          <span className={styles.date}>{formatDate(current.date)}</span>
        </div>
        <Button
          type="button"
          variant="secondary"
          className={styles.navButton}
          onClick={goNext}
          disabled={!canNext}
          aria-label="Next installment"
          data-testid="next-installment"
        >
          <span style={{ transform: 'scaleX(-1)' }}>
            <Asset
              icon={ChevronIcon}
              height={16}
              width={16}
              description="next"
            />
          </span>
        </Button>
      </div>
      <div className={styles.pagination} data-testid="installment-pagination">
        {index + 1} of {installments.length}
      </div>
    </div>
  );
};
